<script setup>
import { computed } from 'vue';
import { messageStamp } from 'shared/helpers/timeHelper';
import { formatReportDuration } from '../constants';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  selectedDay: { type: String, default: null },
});

const emit = defineEmits(['openDetail']);

const formatTs = value => {
  if (!value) return '--';
  try {
    const unix = Math.floor(new Date(value).getTime() / 1000);
    return messageStamp(unix, 'MMM d, HH:mm');
  } catch {
    return value;
  }
};

const tableRows = computed(() => props.rows || []);
</script>

<template>
  <section
    class="relative mt-2 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="flex flex-wrap items-center justify-between gap-2 px-2 py-2">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.TITLE') }}
      </h3>
      <p v-if="selectedDay" class="text-xs text-n-slate-11">
        {{
          $t('AGENT_ACTIVITY_REPORT.SUMMARY.FILTERED_DAY', {
            date: selectedDay,
          })
        }}
      </p>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full min-w-[960px] text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] text-n-slate-11">
            <th class="px-2 py-2 text-left font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.AGENT') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.ACCEPTING') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.NOT_ACCEPTING') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.ONLINE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.OFFLINE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.ONLINE_SHARE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.FIRST_ONLINE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.LAST_OFFLINE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('AGENT_ACTIVITY_REPORT.SUMMARY.STATUS_CHANGES') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tableRows"
            :key="row.user_id"
            class="border-b border-n-weak/60 last:border-b-0"
          >
            <td class="px-2 py-2 text-left">
              <button
                type="button"
                class="text-left text-n-brand hover:underline"
                @click="emit('openDetail', row.user_id)"
              >
                <span class="block font-medium text-n-slate-12">{{
                  row.agent_name
                }}</span>
                <span class="block text-[10px] text-n-slate-11">{{
                  row.email
                }}</span>
              </button>
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatReportDuration(row.accepting_seconds) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatReportDuration(row.not_accepting_seconds) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatReportDuration(row.online_seconds) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatReportDuration(row.offline_seconds) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ row.online_share ?? 0 }}%
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatTs(row.first_online_at_utc) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ formatTs(row.last_offline_at_utc) }}
            </td>
            <td class="px-2 py-2 text-center tabular-nums">
              {{ Number(row.status_change_count || 0).toLocaleString() }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p
      v-if="!tableRows.length"
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('AGENT_ACTIVITY_REPORT.EMPTY') }}
    </p>
  </section>
</template>
